import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sections } from '../data/questions';

const s = {
  page: { paddingTop: '64px', minHeight: '100vh', background: 'var(--cream)' },
  progressHeader: {
    position: 'fixed', top: '64px', left: 0, right: 0, zIndex: 90,
    background: 'rgba(250,250,247,0.96)', backdropFilter: 'blur(8px)',
    borderBottom: '1px solid var(--border)', padding: '12px 48px',
  },
  progressTrack: { height: '3px', background: 'var(--border)', borderRadius: '999px', overflow: 'hidden', marginBottom: '8px' },
  progressFill: (pct) => ({ height: '100%', background: 'var(--navy)', borderRadius: '999px', width: pct + '%', transition: 'width 0.4s ease' }),
  progressMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  progressSection: { fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' },
  progressCount: { fontSize: '0.72rem', color: 'var(--muted)' },
  body: { maxWidth: '680px', margin: '0 auto', padding: '120px 48px 80px' },
  sectionHeader: { marginBottom: '48px' },
  sectionEyebrow: { fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' },
  eyebrowLine: { display: 'block', width: '24px', height: '1px', background: 'var(--amber)' },
  sectionTitle: { fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '8px' },
  sectionSubtitle: { fontSize: '0.9rem', fontWeight: 300, color: 'var(--muted)', lineHeight: 1.7 },
  questionCard: { background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--border)', padding: '32px', marginBottom: '20px' },
  questionText: { fontSize: '1rem', fontWeight: 500, color: 'var(--ink)', marginBottom: '8px', lineHeight: 1.5 },
  questionHelper: { fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 300, marginBottom: '20px', lineHeight: 1.6 },
  questionRequired: { color: 'var(--amber)', marginLeft: '4px' },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  option: (selected, err) => ({
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    padding: '12px 16px', borderRadius: '8px',
    border: `1px solid ${err ? '#FECACA' : selected ? 'var(--navy)' : 'var(--border)'}`,
    background: selected ? '#EFF6FF' : 'var(--white)',
    cursor: 'pointer', transition: 'all 0.15s',
  }),
  optionDot: (sel) => ({
    width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
    border: `2px solid ${sel ? 'var(--navy)' : '#D1D5DB'}`,
    background: sel ? 'var(--navy)' : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }),
  optionCheck: (sel) => ({
    width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, marginTop: '2px',
    border: `2px solid ${sel ? 'var(--navy)' : '#D1D5DB'}`,
    background: sel ? 'var(--navy)' : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', color: 'white', fontWeight: 700,
  }),
  optionLabel: (sel) => ({ fontSize: '0.88rem', fontWeight: sel ? 500 : 400, color: sel ? 'var(--navy)' : 'var(--ink)', lineHeight: 1.5 }),
  errorMsg: { fontSize: '0.78rem', color: '#DC2626', marginTop: '10px' },
  maxNote: { fontSize: '0.75rem', color: 'var(--muted)', marginTop: '8px' },
  navRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '40px', paddingTop: '32px', borderTop: '1px solid var(--border)' },
  btnBack: { fontSize: '0.85rem', fontWeight: 500, color: 'var(--muted)', background: 'none', border: 'none', padding: '10px 0', cursor: 'pointer' },
  btnNext: { fontSize: '0.88rem', fontWeight: 500, background: 'var(--navy)', color: 'var(--white)', border: 'none', padding: '12px 32px', borderRadius: '6px', cursor: 'pointer', letterSpacing: '0.02em' },
  btnSubmit: { fontSize: '0.88rem', fontWeight: 500, background: 'var(--amber)', color: 'var(--white)', border: 'none', padding: '14px 40px', borderRadius: '6px', cursor: 'pointer', letterSpacing: '0.02em' },
  nameCard: {
    background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--border)',
    padding: '32px', marginBottom: '20px',
  },
  openCard: {
    background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--border)',
    padding: '32px', marginBottom: '20px',
  },
  openLabel: { fontSize: '1rem', fontWeight: 500, color: 'var(--ink)', marginBottom: '6px' },
  openHelper: { fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 300, marginBottom: '16px', lineHeight: 1.6 },
  openTextarea: {
    width: '100%', minHeight: '100px', resize: 'vertical',
    border: '1px solid var(--border)', borderRadius: '8px',
    padding: '12px 16px', fontSize: '0.9rem', lineHeight: 1.6,
    color: 'var(--ink)', background: 'var(--white)', outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  nameLabel: { fontSize: '1rem', fontWeight: 500, color: 'var(--ink)', marginBottom: '6px' },
  nameHelper: { fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 300, marginBottom: '16px', lineHeight: 1.6 },
  nameInput: {
    width: '100%', padding: '12px 16px', borderRadius: '8px',
    border: '1px solid var(--border)', fontSize: '0.95rem',
    fontFamily: 'var(--font-body)', color: 'var(--ink)',
    outline: 'none', background: 'var(--cream)', boxSizing: 'border-box',
  },
  compliance: { marginTop: '40px', padding: '16px', background: '#FFFBEB', borderRadius: '8px', fontSize: '0.74rem', color: '#92400E', lineHeight: 1.65 },
  generating: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '64px 48px' },
  genTitle: { fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '12px' },
  genBody: { fontSize: '0.92rem', fontWeight: 300, color: 'var(--muted)', lineHeight: 1.7, maxWidth: '400px', margin: '0 auto 32px' },
  genSteps: { display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '320px', margin: '0 auto' },
  genStep: (active) => ({
    fontSize: '0.82rem', color: active ? 'var(--navy)' : 'var(--muted)',
    fontWeight: active ? 500 : 300, display: 'flex', alignItems: 'center', gap: '10px',
    transition: 'all 0.3s',
  }),
  genDot: (active) => ({
    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
    background: active ? 'var(--amber)' : 'var(--border)',
    transition: 'background 0.3s',
  }),
};

function hasValue(val) {
  if (val === undefined || val === null) return false;
  if (Array.isArray(val)) return val.length > 0;
  return String(val).trim().length > 0;
}

function QuestionCard({ question, value, onChange, otherValue, onOtherChange, showError }) {
  const hasError = showError && question.required && !hasValue(value);

  function handleSingle(opt) { onChange(question.id, opt); }
  function handleMulti(opt) {
    const cur = value || [];
    if (cur.includes(opt)) {
      onChange(question.id, cur.filter(v => v !== opt));
    } else {
      if (question.maxSelect && cur.length >= question.maxSelect) return;
      onChange(question.id, [...cur, opt]);
    }
  }
  function handleText(e) { onChange(question.id, e.target.value); }
  const otherTrigger = question.otherTrigger || 'Other';
  const otherInOptions = question.options && question.options.includes(otherTrigger);
  const otherIsSelected = question.type === 'single'
    ? value === otherTrigger || value === 'Other'
    : Array.isArray(value) && (value.includes(otherTrigger) || value.includes('Other'));

  // Text input type (open question)
  if (question.type === 'text') {
    return (
      <div style={s.questionCard}>
        <div style={s.questionText}>{question.text}</div>
        {question.helper && <div style={s.questionHelper}>{question.helper}</div>}
        <textarea
          value={value || ''}
          onChange={handleText}
          placeholder={question.placeholder || ''}
          rows={4}
          style={{
            width: '100%', padding: '14px 16px', borderRadius: '8px',
            border: '1px solid var(--border)', fontSize: '0.9rem',
            fontFamily: 'var(--font-body)', color: 'var(--ink)',
            lineHeight: 1.6, resize: 'vertical', outline: 'none',
            background: 'var(--cream)',
          }}
        />
      </div>
    );
  }

  return (
    <div style={s.questionCard}>
      <div style={s.questionText}>
        {question.text}
        {question.required && <span style={s.questionRequired}>*</span>}
      </div>
      {question.helper && <div style={s.questionHelper}>{question.helper}</div>}
      <div style={s.optionsGrid}>
        {question.options.map(opt => {
          const sel = question.type === 'single' ? value === opt : (value || []).includes(opt);
          return (
            <div key={opt} style={s.option(sel, hasError)}
              onClick={() => question.type === 'single' ? handleSingle(opt) : handleMulti(opt)}>
              {question.type === 'single'
                ? <div style={s.optionDot(sel)}>{sel && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />}</div>
                : <div style={s.optionCheck(sel)}>{sel && '✓'}</div>
              }
              <span style={s.optionLabel(sel)}>{opt}</span>
            </div>
          );
        })}
      </div>
      {question.allowOther && !otherInOptions && (
        <div
          style={s.option(otherIsSelected, false)}
          onClick={() => question.type === 'single' ? handleSingle(otherTrigger) : handleMulti(otherTrigger)}>
          {question.type === 'single'
            ? <div style={s.optionDot(otherIsSelected)}>{otherIsSelected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />}</div>
            : <div style={s.optionCheck(otherIsSelected)}>{otherIsSelected && '✓'}</div>
          }
          <span style={s.optionLabel(otherIsSelected)}>Other</span>
        </div>
      )}
      {question.allowOther && otherIsSelected && (
        <textarea
          value={otherValue || ''}
          onChange={e => onOtherChange && onOtherChange(question.id + '_other', e.target.value)}
          placeholder="Please describe..."
          rows={2}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: '8px',
            border: '1px solid var(--border)', fontSize: '0.9rem',
            fontFamily: 'var(--font-body)', color: 'var(--ink)',
            lineHeight: 1.6, resize: 'vertical', outline: 'none',
            background: 'var(--cream)', marginTop: '6px',
          }}
        />
      )}
      {hasError && <div style={s.errorMsg}>⚠ Please select an answer to continue.</div>}
      {question.maxSelect && <div style={s.maxNote}>Select up to {question.maxSelect} ({(value || []).length} selected)</div>}
    </div>
  );
}

const GEN_STEPS = [
  'Reading your assessment profile…',
  'Selecting your foundational products…',
  'Activating targeted modules…',
  'Mapping science layer…',
  'Building lifestyle guidance…',
  'Finalizing your protocol…',
];

export default function Assessment({ onComplete, existingAnswers }) {
  const navigate = useNavigate();
  const topRef = useRef(null);

  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState(existingAnswers || {});
  const [showErrors, setShowErrors] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentSection]);

  const section = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;

  const totalRequired = sections.flatMap(s => s.questions).filter(q => q.required).length;
  const answeredRequired = sections.flatMap(s => s.questions)
    .filter(q => q.required && hasValue(answers[q.id])).length;
  const progressPct = Math.round((answeredRequired / totalRequired) * 100);

  function handleChange(qId, val) {
    setAnswers(prev => ({ ...prev, [qId]: val }));
    if (showErrors) setShowErrors(false);
  }

  function validateSection() {
    return section.questions
      .filter(q => q.required)
      .every(q => hasValue(answers[q.id]));
  }

  function handleNext() {
    if (!validateSection()) { setShowErrors(true); return; }
    setShowErrors(false);
    setCurrentSection(prev => prev + 1);
  }

  function handleBack() {
    setShowErrors(false);
    setCurrentSection(prev => prev - 1);
  }

  async function handleSubmit() {
    if (!validateSection()) { setShowErrors(true); return; }
    setGenerating(true);

    // Animate through generation steps
    for (let i = 0; i < GEN_STEPS.length; i++) {
      setGenStep(i);
      await new Promise(r => setTimeout(r, 400));
    }

    try {
      const { generateRecommendations } = await import('../engine/recommendations.js');
      const { generateLifestyleGuidance } = await import('../engine/lifestyle.js');
      const protocol = generateRecommendations(answers);
      const guidance = generateLifestyleGuidance(answers);
      onComplete(answers, protocol, guidance);
    } catch (err) {
      console.error('Engine error:', err);
      console.error('Engine stack:', err.stack);
      onComplete(answers, { error: err.message + '\n\nStack: ' + (err.stack || '') }, null);
    }

    navigate('/results');
  }

  if (generating) {
    return (
      <div style={s.generating}>
        <div style={{ fontSize: '2.5rem', marginBottom: '24px' }}>⚗️</div>
        <h2 style={s.genTitle}>Building your protocol…</h2>
        <p style={s.genBody}>Matching your profile against 94 products and the science layer.</p>
        <div style={s.genSteps}>
          {GEN_STEPS.map((step, i) => (
            <div key={step} style={s.genStep(i <= genStep)}>
              <div style={s.genDot(i <= genStep)} />
              {step}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div ref={topRef} />
      <div style={s.progressHeader}>
        <div style={s.progressTrack}>
          <div style={s.progressFill(progressPct)} />
        </div>
        <div style={s.progressMeta}>
          <span style={s.progressSection}>{currentSection + 1} of {sections.length} — {section.title}</span>
          <span style={s.progressCount}>{progressPct}% complete</span>
        </div>
      </div>

      <div style={s.body}>
        <div style={s.sectionHeader}>
          <div style={s.sectionEyebrow}><span style={s.eyebrowLine} />Section {currentSection + 1} of {sections.length}</div>
          <h2 style={s.sectionTitle}>{section.title}</h2>
          <p style={s.sectionSubtitle}>{section.subtitle}</p>
        </div>

        {currentSection === 0 && (
          <div style={s.nameCard}>
            <div style={s.nameLabel}>What's your first name? <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span></div>
            <div style={s.nameHelper}>Lumen will use it to personalize your protocol and coaching session.</div>
            <input
              type="text"
              value={answers.name || ''}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="First name"
              style={s.nameInput}
              autoComplete="given-name"
            />
          </div>
        )}

        {section.questions.map(q => (
          <QuestionCard key={q.id} question={q} value={answers[q.id]} onChange={handleChange} otherValue={answers[q.id + '_other'] || ''} onOtherChange={handleChange} showError={showErrors} />
        ))}

      {/* Open text — last section only */}
      {isLastSection && (
        <div style={s.openCard}>
          <div style={s.openLabel}>Is there anything else you'd like Lumen to know?</div>
          <div style={s.openHelper}>Optional — health context, goals, concerns, or anything that didn't fit the questions above. Lumen reads this before your first conversation.</div>
          <textarea
            style={s.openTextarea}
            placeholder="e.g. I've been feeling foggy lately, my doctor mentioned my cholesterol is slightly elevated, I wake at 3am most nights..."
            value={answers.q_open || ''}
            onChange={e => handleChange('q_open', e.target.value)}
            onFocus={e => { e.target.style.borderColor = 'var(--navy)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
          />
        </div>
      )}

        <div style={s.navRow}>
          <button style={s.btnBack} onClick={currentSection === 0 ? () => navigate('/') : handleBack}>
            ← {currentSection === 0 ? 'Back to home' : 'Previous'}
          </button>
          {isLastSection
            ? <button style={s.btnSubmit} onClick={handleSubmit}>Build my protocol →</button>
            : <button style={s.btnNext} onClick={handleNext}>Next →</button>
          }
        </div>

        <div style={s.compliance}>
          These statements have not been evaluated by the Food and Drug Administration. Products recommended through this tool are not intended to diagnose, treat, cure, or prevent any disease. Authored by an independent Shaklee distributor — not affiliated with Shaklee Corporation.
        </div>
      </div>
    </div>
  );
}
